import React, { memo, ReactNode } from 'react'
import classNames from 'classnames'
import RightArrowIcon from 'transfix-icons/build/RightArrowIcon'
import { makeStyles, Typography, Box, BoxProps, createStyles, Skeleton } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IconWrapper } from 'transfix-ui'
import { ActionListIcon, TIconName } from './ActionListIcon'

interface IActionListItemProps extends Pick<BoxProps, 'onClick'> {
  /**
   * @example 'Add your first load'
   */
  title?: string
  /**
   * @example 'Learn how to create a planned load and dispatch it to your driver.'
   */
  subtitle?: string
  /**
   * @example <ContextCard triggerText=' Learn more'>An approval was sent to the following email</ContextCard>
   */
  subtitleDetailsComponent?: ReactNode
  /**
   * @example 'warning'
   */
  iconName?: TIconName
  /**
   * Triggers <Skeleton /> values for title and subtitle
   * @default false
   */
  loading?: boolean
  /**
   * @default false
   */
  disabled?: boolean
}

type TClassKey = 'root' | 'hover' | 'textWrapper' | 'title' | 'iconWrapper' | 'arrowRight'

const useActionListItemStyles = makeStyles(theme =>
  createStyles<TClassKey, Pick<IActionListItemProps, 'disabled'>>({
    root: {
      padding: theme.spacing(1.5),
      display: 'flex',
      borderRadius: theme.shape.borderRadius * 2,
      position: 'relative',

      // Displays a bottom border
      '&:after': {
        display: 'block',
        content: '" "',
        position: 'absolute',
        bottom: 0,
        left: 10,
        right: 10,
        width: 'auto',
        borderBottom: `1px solid ${brandColors.coolGray1}`,
      },

      // Removes bottom border for last element
      '&:last-child:after': {
        borderBottom: 'none',
      },
    },
    /**
     * Only added to component when `loading` prop is `false`
     */
    hover: {
      '&:hover': {
        background: brandColors.coolGray1,
        cursor: 'pointer',

        // $title is reference to `title` class in this style hook
        '& $title': {
          color: brandColors.skyBlue7,
        },

        '& $arrowRight': {
          color: brandColors.coolGray6,
        },
      },
    },
    textWrapper: {
      flex: '1 1 auto',
      paddingRight: theme.spacing(2),
    },
    title: {
      color: ({ disabled }) => (disabled ? brandColors.coolGray8 : brandColors.skyBlue6),
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      flex: '0 0 auto',
    },
    arrowRight: {},
  })
)

/**
 * List item in Setup Guide, which accepts different configurations
 */
export const ActionListItem = memo<IActionListItemProps>(
  ({
    title = <Skeleton variant='text' width='65%' />,
    subtitle = <Skeleton variant='text' width='35%' />,
    subtitleDetailsComponent,
    iconName,
    onClick: onClickProp,
    loading: loadingProp = false,
    disabled: disabledProp = false,
  }) => {
    const classes = useActionListItemStyles({ disabled: disabledProp })
    return (
      <Box
        component='li'
        className={classNames(classes.root, {
          [classes.hover]: !loadingProp && !disabledProp,
        })}
        data-test='action-list-item'
        onClick={!loadingProp && !disabledProp ? onClickProp : undefined}>
        <Box className={classes.textWrapper}>
          {/**
           * Title
           */}
          <Box mb={0.5}>
            <Typography variant='subtitle2' component='div' className={classes.title}>
              {title}
            </Typography>
          </Box>
          {/**
           * Subtitle
           */}
          <Box>
            <Typography variant='subtitle2' component='span' color='textSecondary'>
              {subtitle}
            </Typography>
            {subtitleDetailsComponent && <Box component='span'>{subtitleDetailsComponent}</Box>}
          </Box>
        </Box>
        {/**
         * Icons
         */}
        <Box className={classes.iconWrapper}>
          {iconName && (
            <Box pr={1.5} display='flex' alignItems='center'>
              <ActionListIcon iconName={iconName} />
            </Box>
          )}
          <Box display='flex' alignItems='center'>
            <IconWrapper
              Icon={RightArrowIcon}
              color={disabledProp ? 'white' : 'coolGray5'}
              BoxProps={{ display: 'flex', alignItems: 'center' }}
              className={classes.arrowRight}
            />
          </Box>
        </Box>
      </Box>
    )
  }
)
